import * as React from 'react';
import { Paper, Typography, styled } from '@mui/material';
import { TooltipProps } from 'recharts/types/component/Tooltip';
import TooltipValue from './TooltipValue';
import { aggregatePayload } from './payloadHandler';
import { ChartSerie } from '../../ChartsTypes';
import { colors } from './../../../../utils/palette';

export type StringSeries = ChartSerie<string>[];

export type Props = TooltipProps<number, string> & {
  series: StringSeries;
  valueFormatter?: (n: number) => string | number;
  footerValueFormatter?: (n: number) => string | number;
  sx?: React.CSSProperties;
  footer?: 'sum' | 'max';
  applyDefaultSort?: boolean;
  order?: string[];
  noPadding?: boolean;
};

export const TooltipBody: React.FC<Props> = ({
  sx,
  valueFormatter = (n: number) => n,
  footerValueFormatter,
  footer,
  payload,
  series,
  applyDefaultSort = true,
  order,
  noPadding = false,
}) => {
  const getTooltipElements = () => {
    let removeZeroElement = payload;
    if (payload && payload?.length > 1) {
      removeZeroElement = payload.filter((e) => e.value !== 0);
    }
    if (removeZeroElement) {
      if (applyDefaultSort) {
        removeZeroElement = removeZeroElement.sort((a, b) => {
          if (order && order.indexOf(a.dataKey as string) < order.indexOf(b.dataKey as string)) {
            return -1;
          }
          if (order && order.indexOf(a.dataKey as string) > order.indexOf(b.dataKey as string)) {
            return 1;
          }
          return 0;
        });
      } else {
        removeZeroElement = removeZeroElement.sort((a, b) => {
          if (a.value === undefined) return 1;
          if (b.value === undefined) return -1;
          return b.value - a.value;
        });
      }
      return removeZeroElement
        .filter(({ name, color, value }) => {
          if (name === undefined || color === undefined || value === undefined) {
            return false;
          } else {
            return true;
          }
        })
        .map(({ dataKey, name, color, value }) => {
          const correspondingSeries = series.find(({ name }) => name === dataKey);
          const tooltipLabel = correspondingSeries?.tooltipLabel ?? name;
          return (
            <TooltipValue
              key={dataKey}
              label={tooltipLabel || ''}
              value={`${valueFormatter(value || 0)}`}
              color={color || ''}
              opacity={correspondingSeries?.opacity}
              dataKey={dataKey}
              xAxis={removeZeroElement?.[0]?.payload.__xAxis__ || removeZeroElement?.[0]?.payload.name}
            />
          );
        });
    }
  };

  return (
    <TooltipElement style={sx} nopadding={noPadding ? 'true' : undefined}>
      <Typography style={{ fontWeight: 600, color: colors.white }}>{payload?.[0]?.payload.__xAxis__ || payload?.[0]?.payload.name}</Typography>
      {getTooltipElements()}
      {footer === 'sum' ? (
        <div>
          <Typography style={{ fontSize: 12, color: colors.white }} variant="body2">
            {'Total: ' + (footerValueFormatter ? footerValueFormatter(aggregatePayload(payload, 'sum')) : aggregatePayload(payload, 'sum'))}
          </Typography>
        </div>
      ) : null}
      {footer === 'max' ? (
        <div>
          <Typography style={{ fontSize: 12, color: colors.white }} variant="body2">
            {'Total: ' + (footerValueFormatter ? footerValueFormatter(aggregatePayload(payload, 'max')) : aggregatePayload(payload, 'max'))}
          </Typography>
        </div>
      ) : null}
    </TooltipElement>
  );
};

export default TooltipBody;

export const TooltipElement = styled(Paper)<{ nopadding?: 'true' }>(({ theme, nopadding }) => ({
  padding: nopadding === 'true' ? theme.spacing(1) : theme.spacing(2.5),
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.common.white,
  transform: nopadding === 'true' ? 'translate(-200px, 5px)' : 'translate(-31px, 8px)',
}));
